import { httpUploadFile } from '@services/upload.http.ts'
import { type FC, type ReactElement, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTrigger
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import {
	FileIcon,
	InfoFillCircleIcon,
	UploadIcon,
	XCloseIcon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useConfirmCsvUpload } from '@/features/luxe-submissions/queries/use-confirm-csv-upload'
import { useCreateCsvUpload } from '@/features/luxe-submissions/queries/use-create-csv-upload'
import { useGetCsvTemplate } from '@/features/luxe-submissions/queries/use-get-csv-template'
import { cn, errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

const MAX_SIZE_BYTES = 10 * 1024 * 1024

interface CsvUploadDialogProps {
	trigger: ReactElement
}

export const CsvUploadDialog: FC<CsvUploadDialogProps> = ({ trigger }) => {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [file, setFile] = useState<File | null>(null)
	const [isUploading, setIsUploading] = useState(false)

	const createMutation = useCreateCsvUpload()
	const confirmMutation = useConfirmCsvUpload()
	const { data: template, isLoading: isTemplateLoading } =
		useGetCsvTemplate(open)

	const resetState = () => {
		setFile(null)
		setIsUploading(false)
	}

	const handleOpenChange = (open: boolean) => {
		setOpen(open)
	}

	const dropzoneState = useDropzone({
		disabled: isUploading,
		accept: { 'text/csv': ['.csv'] },
		maxFiles: 1,
		multiple: false,
		maxSize: MAX_SIZE_BYTES,
		onDropAccepted: (files) => setFile(files[0]),
		onDropRejected: (rejected) => {
			const error = rejected[0]?.errors[0]
			if (error?.code === 'file-too-large') {
				toast.error(
					t('luxe_submissions:csv_upload_error_file_too_large', {
						maxSize: MAX_SIZE_BYTES / (1024 * 1024)
					})
				)
				return
			}
			toast.error(t('luxe_submissions:csv_upload_error_invalid_type'))
		}
	})

	const handleUpload = async () => {
		if (!file) {
			return
		}
		setIsUploading(true)
		try {
			const created = await createMutation.mutateAsync(file.name)
			await httpUploadFile({
				url: created.preSignedUrl,
				payload: file
			})
			await confirmMutation.mutateAsync(created.id)
			resetState()
			handleOpenChange(false)
		} catch (error) {
			errorHandler(error)
		} finally {
			setIsUploading(false)
		}
	}

	return (
		<Dialog onOpenChange={handleOpenChange} open={open}>
			<DialogTrigger render={trigger} />
			<DialogContent className='max-w-120' showCloseButton={false}>
				<DialogHeader className='items-center'>
					{t('luxe_submissions:csv_upload_dialog_title')}
				</DialogHeader>

				<DialogBody>
					<Dropzone
						className={cn(
							'flex min-h-50 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-neutral-300 border-dashed px-6 py-10 text-center transition-colors',
							dropzoneState.isDragActive
								? 'border-neutral-900 bg-neutral-200'
								: 'hover:border-neutral-900',
							isUploading &&
								'cursor-not-allowed border-neutral-300 bg-inherit hover:border-neutral-300'
						)}
						state={dropzoneState}
					>
						{file ? (
							<div className='flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3'>
								<FileIcon className='shrink-0 text-neutral-600' />
								<div className='flex flex-1 flex-col items-start overflow-hidden'>
									<p className='max-w-full truncate font-medium text-neutral-900 text-sm'>
										{file.name}
									</p>
									<p className='text-neutral-600 text-xs'>
										{(file.size / 1024).toFixed(1)} KB
									</p>
								</div>
								<button
									className='shrink-0 rounded-full p-1 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700'
									disabled={isUploading}
									onClick={(e) => {
										e.stopPropagation()
										setFile(null)
									}}
									type='button'
								>
									<XCloseIcon size={16} />
								</button>
							</div>
						) : (
							<>
								<div className='mb-2 flex size-20 items-center justify-center rounded-2xl bg-neutral-100'>
									<UploadIcon
										className='text-neutral-700'
										size={32}
									/>
								</div>

								<div>
									<p className='font-bold text-lg text-neutral-900'>
										{t(
											'luxe_submissions:csv_upload_dialog_drop_hint'
										)}
									</p>
									<p className='text-base text-neutral-700'>
										{t(
											'luxe_submissions:csv_upload_dialog_or'
										)}{' '}
										<span className='font-semibold text-neutral-900 underline'>
											{t(
												'luxe_submissions:csv_upload_dialog_browse'
											)}
										</span>
									</p>
								</div>
								<div className='mt-2 inline-flex items-center gap-2 rounded-2xl bg-neutral-100 px-4 py-1 text-neutral-500 text-sm'>
									<FileIcon size={16} />
									{t(
										'luxe_submissions:csv_upload_dialog_file_constraints',
										{
											maxSize:
												MAX_SIZE_BYTES / (1024 * 1024)
										}
									)}
								</div>
							</>
						)}
					</Dropzone>

					<div className='flex items-start gap-2 rounded-lg bg-blue-50 px-4 py-6'>
						<InfoFillCircleIcon
							className='mt-0.5 shrink-0 text-neutral-900'
							size={16}
						/>
						<div className='flex flex-col text-base'>
							<p className='font-semibold text-neutral-900'>
								{t(
									'luxe_submissions:csv_upload_dialog_how_to_prepare_title'
								)}
							</p>
							<div className='text-neutral-700'>
								{t(
									'luxe_submissions:csv_upload_dialog_how_to_prepare_description'
								)}{' '}
								<ButtonLegacy
									asChild
									className='inline-flex h-auto px-0'
									disabled={isTemplateLoading && !template}
									variant='tertiary'
								>
									<a
										download={template?.fileName ?? ''}
										href={template?.url ?? ''}
									>
										{t(
											'luxe_submissions:csv_upload_dialog_template_link'
										)}
									</a>
								</ButtonLegacy>
							</div>
						</div>
					</div>
				</DialogBody>

				<DialogFooter className='flex flex-row justify-between bg-neutral-white'>
					<DialogClose
						render={
							<ButtonLegacy type='button' variant='tertiary' />
						}
					>
						{t('common:cancel')}
					</DialogClose>
					<ButtonLegacy
						disabled={!file || isUploading}
						onClick={handleUpload}
						size='md'
						type='button'
						variant='brand'
					>
						{t('common:upload')}
						{isUploading && <Spinner />}
					</ButtonLegacy>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
